import { PaperClipIcon, MagnifyingGlassIcon, ArrowDownIcon, DocumentIcon } from '@heroicons/react/24/solid'; // Added ArrowDownIcon
import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { useSocket } from "../../components/SocketContext";
import { sendMessage, getChatMessages, getChatList, setSelectedChat, unSetSelectedChat, offlineUser, getGroupDetails } from '@/store/slices/chatSlice';
import Toaster from '@/components/Toaster';
import ShowLoader from '@/components/Loader';
import FileModal from './chat-file-modal';
import FilePreview from '@/components/FilePreview';
import { useTranslation } from 'react-i18next';
import GroupInfoModal from './group-info-modal';



// export function ChatArea() {
    export function ChatArea({ onOpenInfo }) {
    const [showGroupInfo, setShowGroupInfo] = useState(false);
    const socket = useSocket();
    const dispatch = useDispatch();
    const { t } = useTranslation('chat');

    // Refs
    const scrollContainerRef = useRef(null);
    const fileInputRef = useRef(null);
    const prevScrollHeightRef = useRef(null);
    const isInitialLoadRef = useRef(true);
    const { selectedChat, loading, group_details } = useSelector((state) => state.chat);
    const selectedConvIdRef = useRef(selectedChat?.conversation_id);

    // State
    const [message, setMessage] = useState("");
    const [allMessages, setAllMessages] = useState([]);
    const [previewFile, setPreviewFile] = useState(null);
    const [viewingFile, setViewingFile] = useState(null);
    const [showScrollButton, setShowScrollButton] = useState(false);

    // Pagination State
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const { user } = useSelector((state) => state.user);

    selectedConvIdRef.current = selectedChat?.conversation_id;

    // --- Helper: Scroll to Bottom ---
    const scrollToBottom = (behavior = "auto") => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({
                top: scrollContainerRef.current.scrollHeight,
                behavior: behavior,
            });
            setShowScrollButton(false); // Hide button immediately on click
        }
    };

    // --- Helper: Check if user is near bottom ---
    const isUserAtBottom = () => {
        if (!scrollContainerRef.current) return true;
        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        return scrollHeight - scrollTop - clientHeight < 150;
    };

    // 1. Reset everything when switching chats
    useEffect(() => {
        if (selectedChat) {
            setAllMessages([]);
            setPage(1);
            setHasMore(true);
            setIsLoading(false);
            setShowScrollButton(false); // Reset button
            setShowGroupInfo(false);
            isInitialLoadRef.current = true;
            fetchMessages(1, true);

            if (selectedChat.type === 'group' && selectedChat.chat_with?.id) {
            dispatch(getGroupDetails(selectedChat.chat_with.id));
            }
        }

    }, [selectedChat?.conversation_id]);

    // 2. Fetch Messages (ignores result if user switched chat before response)
    const fetchMessages = async (pageNum, isInitialLoad = false) => {
        if (isLoading || !selectedChat?.conversation_id) return;
        const convId = selectedChat.conversation_id;
        setIsLoading(true);

        try {
            if (!isInitialLoad && scrollContainerRef.current) {
                prevScrollHeightRef.current = scrollContainerRef.current.scrollHeight;
            } else {
                prevScrollHeightRef.current = null;
            }

            const response = await dispatch(getChatMessages({
                id: convId,
                page: pageNum
            })).unwrap();

            if (selectedConvIdRef.current !== convId) return;

            const newMessages = response?.chatData || [];
            if (newMessages?.length < 10) setHasMore(false);
            const reversedMessages = [...newMessages].reverse();

            setAllMessages((prev) => {
                if (isInitialLoad) return reversedMessages;
                return [...reversedMessages, ...prev];
            });
        } catch (error) {
            if (selectedConvIdRef.current === convId) {
                console.error("Failed to fetch messages", error);
            }
        } finally {
            if (selectedConvIdRef.current === convId) setIsLoading(false);
        }
    };

    // 3. HANDLE SCROLL POSITION LOGIC
    useLayoutEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        if (isInitialLoadRef.current && allMessages?.length > 0) {
            container.scrollTop = container.scrollHeight;
            isInitialLoadRef.current = false;
            return;
        }

        if (prevScrollHeightRef.current !== null && !isLoading) {
            const newScrollHeight = container.scrollHeight;
            const diff = newScrollHeight - prevScrollHeightRef.current;
            container.scrollTop = diff;
            prevScrollHeightRef.current = null;
        }
    }, [allMessages, isLoading]);


    // 4. Scroll Listener (UPDATED)
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;

        // Logic for Showing "Scroll to Bottom" Button
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        setShowScrollButton(distanceFromBottom > 200); // Show if more than 200px up

        // Logic for Pagination
        if (scrollTop === 0 && hasMore && !isLoading) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchMessages(nextPage, false);
        }
    };

    // Read/seen indicator: update message status when recipient has read (blue tick)
    useEffect(() => {
        if (!socket || !selectedChat?.conversation_id) return;
        const currentConvId = selectedChat.conversation_id;
        const handleReadSeenEvent = (payload) => {
            const convId = payload?.conversation_id ?? payload?.data?.conversation_id;
            if (convId == null) return;
            if (Number(convId) !== Number(currentConvId)) return;
            setAllMessages((prev) =>
                prev.map((msg) =>
                    msg.status === "seen" ? msg : { ...msg, status: "seen" }
                )
            );
        };
        socket.on("read_seen_event", handleReadSeenEvent);
        return () => socket.off("read_seen_event", handleReadSeenEvent);
    }, [socket, selectedChat?.conversation_id]);

    // Delivered indicator: update message status when message is delivered to recipient
    useEffect(() => {
        if (!socket || !selectedChat?.conversation_id) return;
        const currentConvId = selectedChat.conversation_id;
        const handleDeliveredEvent = (payload) => {
            const convId = payload?.conversation_id ?? payload?.data?.conversation_id;
            const messageId = payload?.message_id ?? payload?.data?.message_id ?? payload?.id;
            if (convId == null || Number(convId) !== Number(currentConvId)) return;
            if (messageId != null) {
                setAllMessages((prev) =>
                    prev.map((m) =>
                        Number(m?.id) === Number(messageId) && m.status !== "seen"
                            ? { ...m, status: "delivered" }
                            : m
                    )
                );
            } else {
                setAllMessages((prev) =>
                    prev.map((m) =>
                        m.status === "sent" ? { ...m, status: "delivered" } : m
                    )
                );
            }
        };
        socket.on("delivered_event", handleDeliveredEvent);
        socket.on("message_delivered", handleDeliveredEvent);
        return () => {
            socket.off("delivered_event", handleDeliveredEvent);
            socket.off("message_delivered", handleDeliveredEvent);
        };
    }, [socket, selectedChat?.conversation_id]);

    // Generic message status update (backend may emit message_status_update with { message_id, conversation_id, status })
    useEffect(() => {
        if (!socket || !selectedChat?.conversation_id) return;
        const currentConvId = selectedChat.conversation_id;
        const handleStatusUpdate = (payload) => {
            const data = payload?.data || payload;
            const convId = data?.conversation_id ?? payload?.conversation_id;
            const messageId = data?.message_id ?? data?.id ?? payload?.message_id;
            const status = data?.status ?? payload?.status;
            if (convId == null || Number(convId) !== Number(currentConvId)) return;
            if (messageId == null || !status) return;
            setAllMessages((prev) =>
                prev.map((m) =>
                    Number(m?.id) === Number(messageId) ? { ...m, status } : m
                )
            );
        };
        socket.on("message_status_update", handleStatusUpdate);
        socket.on("status_update", handleStatusUpdate);
        return () => {
            socket.off("message_status_update", handleStatusUpdate);
            socket.off("status_update", handleStatusUpdate);
        };
    }, [socket, selectedChat?.conversation_id]);

    // 5. Socket Listener
    useEffect(() => {
        if (!socket) return;

        const handleReceiveMessage = (newMessage) => {
            const msgData = newMessage?.data || newMessage;
            const currentConvId = selectedChat?.conversation_id;
            const isRelevant = currentConvId != null && (
                (selectedChat?.type === "group" && msgData?.conversation_id == currentConvId) ||
                (msgData?.conversation_id == currentConvId)
            );

            if (isRelevant) {
                const shouldScroll = isUserAtBottom() || Number(msgData?.sender_id) === Number(user?.id);
                setAllMessages((prev) => {
                    if (prev.some(m => m.id === msgData.id)) return prev;
                    return [...prev, msgData];
                });

                if (shouldScroll) {
                    setTimeout(() => scrollToBottom("smooth"), 100);
                } else {
                    // If we didn't scroll automatically (user is reading old history), 
                    // ensure the button is visible so they know there is a new message
                    setShowScrollButton(true);
                }
            }
            if (!currentConvId || msgData?.conversation_id !== currentConvId) {
                dispatch(getChatList({ page: 1 }));
            }

            const status = msgData?.status;
            if ((status === "seen" || status === "delivered") && msgData?.id != null && currentConvId != null && msgData?.conversation_id == currentConvId) {
                setAllMessages((prev) =>
                    prev.map((m) =>
                        Number(m?.id) === Number(msgData.id) ? { ...m, status } : m
                    )
                );
            }
        };

        socket.on("receive_message", handleReceiveMessage);
        socket.on("send_group_message", handleReceiveMessage);
        socket.on("send_message", handleReceiveMessage);

        return () => {
            socket.off("receive_message", handleReceiveMessage);
            socket.off("send_group_message", handleReceiveMessage);
            socket.off("send_message", handleReceiveMessage);
        };
    }, [socket, selectedChat, user?.id]);

    const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1 GB

    const ALLOWED_TYPES = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/webp",
    ];

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type) {
            Toaster.error("Folders are not supported");
            e.target.value = "";
            return;
        }

        if (!ALLOWED_TYPES.includes(file.type)) {
            Toaster.error("Only PDF and image files are allowed");
            e.target.value = "";
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            Toaster.error("File size must be less than 1 GB");
            e.target.value = "";
            return;
        }

        setPreviewFile(file);
    };


    const cancelPreview = () => {
        setPreviewFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const getMessageType = (file) => {
        if (!file) return "text";
        if (file.type.startsWith("image/")) return "image";
        if (file.type.startsWith("video/")) return "video";
        return "file";
    };

    const handleSendMessage = async () => {
        if (!message.trim() && !previewFile) return;

        const formData = new FormData();
        formData.append("sender_id", user?.id);

        if (selectedChat?.type === "group") {
            formData.append("group_id", selectedChat?.chat_with?.id);
        } else {
            formData.append("receiver_id", selectedChat?.chat_with?.id);
        }

        formData.append("message", message);
        formData.append("type", selectedChat?.type);
        formData.append("created_at", new Date().toISOString());

        if (previewFile) {
            formData.append("file", previewFile);
            formData.append("message_type", getMessageType(previewFile));
        } else {
            formData.append("message_type", "text");
        }

        try {
            const serverResponse = await dispatch(sendMessage(formData)).unwrap();
            const messageWithStatus = {
                ...serverResponse,
                status: serverResponse?.status || "sent",
            };
            setAllMessages((prev) => [...prev, messageWithStatus]);

            if (socket?.connected) {
                if (selectedChat?.type === "group") {
                    socket.emit("send_group_message", serverResponse);
                } else {
                    socket.emit("send_message", serverResponse);
                }
            }

            setMessage("");
            cancelPreview();

            if (selectedChat?.isNewChatCreated) {
                dispatch(getChatList({ page: 1 })).unwrap().then((payload) => {
                    const list = payload?.chatList || [];
                    const conversationId = serverResponse?.conversation_id;
                    const updatedChat = list.find((c) => Number(c.conversation_id) === Number(conversationId));
                    if (updatedChat) {
                        dispatch(setSelectedChat(updatedChat));
                    }
                });
            }

            setTimeout(() => scrollToBottom("smooth"), 50);

        } catch (error) {
            console.error(error);
        }
    };

    const DoubleTick = ({ color = "#9CA3AF" }) => (
        <svg width="18" height="14" viewBox="0 0 20 16" fill={color}>
            <path d="M1 9l4 4L14 3l-1.4-1.4L5 11 2.4 8.4z" />
            <path d="M7 9l4 4L20 3l-1.4-1.4L11 11 8.4 8.4z" />
        </svg>
    );

    const SingleTick = ({ color = "#9CA3AF" }) => (
        <svg width="14" height="14" viewBox="0 0 16 16" fill={color}>
            <path d="M6.5 11.5L3 8l1.4-1.4L6.5 8.7 11.6 3.6 13 5z" />
        </svg>
    );

const formatChatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();

        const isToday = date.toDateString() === now.toDateString();

        const yesterday = new Date();
        yesterday.setDate(now.getDate() - 1);
        const isYesterday = date.toDateString() === yesterday.toDateString();

        const timeOptions = {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true, // 12-hour format with AM/PM
        };
        const formattedTime = date.toLocaleTimeString(undefined, timeOptions);

        if (isToday) return `${t('today')}   ${formattedTime}`;
        if (isYesterday) return `${t('yesterday')}   ${formattedTime}`;

        return date.toLocaleDateString(undefined, {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
        };
const toSentenceCase = (text) => {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};




    const ChatBubble = ({ text, time, isUser, isSeen, type, fileUrl, fileName,sender_name }) => {
        const isAttachment = ["file", "image", "video"].includes(type) || !!fileUrl;
        const isImage = type === "image" || fileUrl?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
        const isPDF = fileUrl?.toLowerCase().endsWith(".pdf") || type === "pdf";

        return (
            <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-1 group`}>
                <div className={`relative max-w-[75%] md:max-w-[65%] ${isUser
                    ? "bg-[#DCF8C6] text-gray-900 rounded-lg rounded-tr-none shadow-sm"
                    : "bg-white text-gray-900 rounded-lg rounded-tl-none shadow-sm"
                    }`}
                    style={{
                        borderRadius: isUser 
                            ? '7.5px 7.5px 0px 7.5px' 
                            : '7.5px 7.5px 7.5px 0px'
                    }}
                >
                    {/* Content Container */}
                    <div className="px-2 py-1.5 pb-0.5">
                        {selectedChat?.type=="group" && !isUser && sender_name && (
                            <h1 className="text-[11px] font-semibold text-[#075E54] mb-0.5 px-0.5">
                            {toSentenceCase(sender_name || t('unknown'))}
                            </h1>
                        )}

                        {isAttachment ? (
                            <div
                                className="cursor-pointer"
                                onClick={() => setViewingFile({ url: fileUrl, type: isPDF ? "pdf" : type })}
                            >
                                
                                {isImage ? (
                                    <div className="relative overflow-hidden rounded-t-md group/img -mx-2 -mt-1.5 mb-1">
                                        <img
                                            src={fileUrl}
                                            alt="attachment"
                                            className="w-full max-w-[280px] max-h-[300px] object-cover"
                                        />
                                        {/* Glass Overlay on Hover */}
                                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
                                            <div className="bg-white/90 p-1.5 rounded-full">
                                                <MagnifyingGlassIcon className="h-4 w-4 text-gray-800" />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={`flex items-center gap-2 p-2 rounded-md mb-1 ${isUser
                                        ? "bg-white/30"
                                        : "bg-gray-50"
                                        }`}>
                                        <div className={`p-1.5 rounded ${isUser ? "bg-white/50" : "bg-gray-200"}`}>
                                            {isPDF ? (
                                                <DocumentIcon className={`h-4 w-4 ${isUser ? "text-gray-700" : "text-gray-600"}`} />
                                            ) : (
                                                <PaperClipIcon className={`h-4 w-4 ${isUser ? "text-gray-700" : "text-gray-600"}`} />
                                            )}
                                        </div>
                                        <div className="flex flex-col min-w-0 flex-1">
                                            <span className={`text-xs font-medium truncate leading-tight ${isUser ? "text-gray-900" : "text-gray-900"}`}>
                                                {fileName}
                                            </span>
                                            <span className={`text-[9px] text-gray-600 mt-0.5`}>
                                                {isPDF ? t('pdfDocument') : t('attachment')}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Text accompanying the attachment - appears below image */}
                                {text && <p className={`text-[14.2px] leading-[19px] pt-1 px-0.5 break-words ${isUser ? "text-gray-900" : "text-gray-900"}`}>{text}</p>}
                            </div>
                        ) : (
                            /* Text-only bubble */
                            <p className={`text-[14.2px] leading-[19px] whitespace-pre-wrap break-words px-0.5 ${isUser ? "text-gray-900" : "text-gray-900"}`}>
                                {text}
                            </p>

                        )}
                    </div>

                    {/* Footer: Time & Status */}
                   <div 
                        className={`flex items-center justify-end gap-1 px-2 pb-1 text-[11px] text-gray-600 w-full`}
                    >   
                        <span className="text-[11px]">
                            {new Date(time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
                        </span>                      
                        {isUser && (
                            <div className="flex items-center">
                                {isSeen === 'seen' && <DoubleTick color="#53BDEB" />}
                                {isSeen === "delivered" && <DoubleTick color="#999999" />}
                                {isSeen === "sent" && <SingleTick color="#999999" />}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    if (!selectedChat) {
        return (
            <div className="flex-grow flex flex-col h-full overflow-hidden bg-[#EFEAE2] relative">
                <div className="flex items-center justify-center h-full">
                    <div className="text-center px-6">
                        <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-4">
                            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('selectAConversation')}</h3>
                        <p className="text-sm text-gray-500">{t('chooseChat')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex w-full h-full overflow-hidden">
        <div className="flex-grow flex flex-col h-full overflow-hidden bg-[#EFEAE2] relative">
            {/* {loading && <ShowLoader />} */}

            <FileModal
                isOpen={!!viewingFile}
                fileUrl={viewingFile?.url}
                type={viewingFile?.type}
                onClose={() => setViewingFile(null)}
            />

            {/* Chat Header */}
            <div className="h-[60px] flex-shrink-0 flex items-center justify-between px-4 bg-[#065C36] border-b border-[#075E54] shadow-sm">
                <div className="flex items-center gap-3">
                    {selectedChat?.chat_with?.image ? (
                        <div className="w-10 h-10 rounded-full overflow-hidden">
                            <img src={selectedChat.chat_with.image} alt={selectedChat.chat_with.name} className="w-full h-full object-cover" />
                        </div>
                    ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-sm font-bold text-gray-600">
                            {selectedChat?.chat_with?.name?.substring(0, 2).toUpperCase()}
                        </div>
                    )}
                    <div>
                        <div className="flex items-center gap-1.5">
                            <h3 className="text-[16px] font-medium text-white">{selectedChat?.chat_with?.name}</h3>

                            {/* Info Icon Button */}
                            {selectedChat?.type === 'group' && (
                            <button
                                type="button"
                                // onClick={() => setShowGroupInfo(true)}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onOpenInfo) {
                                        onOpenInfo();
                                    } else {
                                        setShowGroupInfo(true);
                                    }
                                }}
                                                            className="text-white/80 hover:text-white transition-colors focus:outline-none"
                                title={t('groupInfo') || 'Group Info'}
                                aria-label="Group Info"
                            >
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    className="h-4 w-4" 
                                    fill="none" 
                                    viewBox="0 0 24 24" 
                                    stroke="currentColor" 
                                    strokeWidth={2}
                                >
                                    <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                                    />
                                </svg>
                            </button>
                            )}
                        </div>
                        {selectedChat?.type === 'group' && (
                            <p className="text-[13px] text-white/80">
                                {t('groupMembers', { 
                                    // count: group_details?.group_members?.length || 0 
                                    count: (group_details?.data?.group_members || group_details?.group_members)?.length || 0
                                    })}
                            </p>
                        )}
                    </div>
                </div>
                <button
                    onClick={() => {
                        dispatch(unSetSelectedChat());
                        dispatch(offlineUser());
                    }}
                    className="p-2 rounded-full text-white/90 hover:bg-white/20 hover:text-white transition-colors"
                    title={t('closeChat')}
                    aria-label="Close chat"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
                
            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="flex-grow overflow-y-auto px-2 py-1 custom-scrollbar relative"
                style={{ 
                    overflowAnchor: 'auto',
                    backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23f0f2f5\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                    backgroundColor: '#EFEAE2'
                }}
            >
                <div className="flex flex-col">
                    {isLoading && page > 1 && (
                        <div className="flex justify-center mb-4">
                            <div className="h-6 w-6 border-2 border-gray-400 border-t-[#065C36] rounded-full animate-spin"></div>
                        </div>
                    )}

                    {allMessages?.length <= 0 && (
                        <div className="text-center text-xs text-gray-500 mb-4">

                        </div>
                    )}

                    {!hasMore && allMessages?.length > 0 && (
                        <div className="text-center text-xs text-gray-500 mb-4">
                            {/* Start of conversation */}
                        </div>
                    )}

                    {allMessages?.length <= 0 && (
                    <div className="flex flex-col h-full items-center justify-center text-center px-6">
                        <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('noMessagesYet')}</h3>
                        <p className="text-sm text-gray-500">{t('startConversation')}</p>
                    </div>
                    )}


                    {allMessages.map((msg, index) => {
                        const showDateSeparator =
                            index === 0 ||
                            new Date(msg.created_at).toDateString() !==
                            new Date(allMessages[index - 1].created_at).toDateString();

                        return <>
                            {
                                showDateSeparator && (
                                    <div className="flex items-center justify-center my-3">
                                        <div className="px-2.5 py-0.5 bg-white/80 backdrop-blur-sm rounded-full border border-gray-200 shadow-sm">
                                            <span className="text-[10px] font-semibold text-gray-600">
                                                {formatChatDate(msg.created_at)}
                                            </span>
                                        </div>
                                    </div>
                                )
                            }
                            <ChatBubble
                                key={msg?.id || index}
                                text={msg?.message}
                                time={msg?.created_at}
                                isUser={Number(msg?.sender_id) === Number(user?.id)}
                                isSeen={msg?.status}
                                type={msg?.message_type || "text"}
                                fileUrl={msg?.file || msg?.file_url}
                                fileName={msg?.file || msg?.file_name}
                                sender_name={msg?.sender_name || t('unknown')}
                                group_type={msg?.type || "user"}
                            />
                        </>
                    })}
                </div>
            </div>

            {/* --- NEW SCROLL TO BOTTOM BUTTON --- */}
            {showScrollButton && (
                <button
                    onClick={() => scrollToBottom("smooth")}
                    className="absolute bottom-24 right-6 z-20 p-2.5 bg-white text-[#065C36] rounded-full shadow-lg border border-gray-200 hover:bg-gray-50 transition-all hover:scale-110 active:scale-95"
                    aria-label="Scroll to bottom"
                    title={t('scrollToBottom')}
                >
                    <ArrowDownIcon className="h-4 w-4" />
                </button>
            )}

            {/* Input Area */}
            <div className="p-2 bg-[#065C36] border-t border-gray-300">
                {!loading && previewFile && (
                    <div className="mb-2 relative inline-block">
                        <FilePreview
                            file={previewFile}
                            onRemove={() => cancelPreview()}
                            isView={false}
                            showDownload={false}
                            containerClass="relative w-48 h-24 rounded-lg overflow-hidden border-2 border-gray-200 shadow-sm"
                        />
                    </div>
                )}

                <div className="flex items-end gap-2">
                    <div className="relative flex-grow flex items-center bg-white rounded-full px-3 py-2 border border-gray-300">
                        <input
                            disabled={loading}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessage()}
                            type="text"
                            placeholder={t('typeMessage')}
                            className="flex-grow p-0 px-2 text-[15px] bg-transparent outline-none text-gray-900 placeholder-gray-500"
                        />
                    </div>
                    <input 
                        type="file"
                        accept="image/*,.pdf"
                        ref={fileInputRef} 
                        className="hidden" 
                        onChange={handleFileChange} 
                    />
                    <button 
                        onClick={() => fileInputRef.current?.click()} 
                        className="p-2 text-white hover:text-gray-800 rounded-full transition-all"
                        title={t('attachFile')}
                    >
                        <PaperClipIcon className="h-5 w-5" />
                    </button>

                    <button 
                        onClick={handleSendMessage}
                        disabled={loading || (!message.trim() && !previewFile)}
                        className={`w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full transition-all ${
                            (message.trim() || previewFile) && !loading
                                ? "bg-[#25D366] hover:bg-[#20BA5A] text-white"
                            : "bg-[#F4B11F] text-white cursor-not-allowed"
                        }`}
                        title={t('sendMessage')}
                    >
                        {!loading && (
                            <svg className="w-5 h-5 transform rotate-45" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                            </svg>
                        )}
                        {loading && (
                            <svg aria-hidden="true" className="inline w-4 h-4 text-white animate-spin" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>
        </div>        
        </div>
    );
}